import React from "react"
import { useAccessoriesStockCurrentReport } from "hooks/useAccessoriesStock"
import jsPDF from "jspdf"
import autoTable from "jspdf-autotable"
import ThemeModal from "components/Common/ThemeModal"
import { Button } from "reactstrap"

const AccessoriesStockReport = () => {
  const { data } = useAccessoriesStockCurrentReport()

  const handleDownloadPDF = () => {
    if (!data?.data?.length) return

    const doc = new jsPDF("p", "mm", "a4")

    const title = "Accessories Stock Report"
    const generatedAt = new Date().toLocaleString()

    autoTable(doc, {
      // ✅ Full width table with safe margins
      margin: { top: 28, left: 10, right: 10, bottom: 18 },

      tableWidth: "auto", // ✅ stretches to full page width

      head: [
        ["ID", "Name", "Total Purchased", "Total Used", "Available Stock"],
      ],

      body: data.data.map(item => [
        String(item.id),
        String(item.name),
        String(item.total_purchased),
        String(item.total_used),
        String(item.available_stock),
      ]),

      styles: {
        fontSize: 9,
        cellPadding: 3,
        overflow: "linebreak", // ✅ text wrap
        cellWidth: "wrap", // ✅ text wrap
        valign: "middle",
      },

      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontStyle: "bold",
        halign: "center",
      },

      didParseCell: cellData => {
        // Highlight zero or negative stock
        if (
          cellData.section === "body" &&
          cellData.column.index === 4 &&
          Number(cellData.cell.raw) <= 0
        ) {
          cellData.cell.styles.textColor = [220, 53, 69]
          cellData.cell.styles.fontStyle = "bold"
        }
      },

      // ✅ Header + Footer on every page
      didDrawPage: () => {
        const pageWidth = doc.internal.pageSize.getWidth()
        const pageHeight = doc.internal.pageSize.getHeight()
        const pageNumber = doc.internal.getNumberOfPages()

        // ---------- HEADER ----------
        doc.setFontSize(14)
        doc.setFont("helvetica", "bold")
        doc.text(title, pageWidth / 2, 16, { align: "center" })

        // ---------- FOOTER ----------
        doc.setFontSize(8)
        doc.setFont("helvetica", "normal")

        doc.text(`Generated: ${generatedAt}`, 10, pageHeight - 8, {
          align: "left",
        })

        doc.text(`Page ${pageNumber}`, pageWidth - 10, pageHeight - 8, {
          align: "right",
        })
      },
    })

    doc.save(
      `${new Date().toISOString().split("T")[0]}-accessories-stock-report.pdf`
    )
  }

  const [modal, setModal] = React.useState(false)
  const toggleModal = () => setModal(!modal)

  return (
    <>
      <div>
        <Button
          color="warning"
          onClick={toggleModal}
          title="View Accessories Stock Report"
        >
          Stock Report
        </Button>
      </div>
      <ThemeModal
        isOpen={modal}
        toggle={toggleModal}
        title={"Accessories Stock Report"}
        size={"lg"}
      >
        <div className="text-center">
          <Button onClick={handleDownloadPDF} color="success" className="mb-3">
            Download PDF
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full border border-gray-200 shadow-sm rounded-lg">
            <thead className="bg-gray-100">
              <tr>
                <th className="py-2 px-4 border">ID</th>
                <th className="py-2 px-4 border">Name</th>
                <th className="py-2 px-4 border">Total Purchased</th>
                <th className="py-2 px-4 border">Total Used</th>
                <th className="py-2 px-4 border">Available Stock</th>
              </tr>
            </thead>
            <tbody>
              {data?.data?.map((item, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="py-2 px-4 border">{item.id}</td>
                  <td className="py-2 px-4 border">{item.name}</td>
                  <td className="py-2 px-4 border">{item.total_purchased}</td>
                  <td className="py-2 px-4 border">{item.total_used}</td>
                  <td
                    className={`py-2 px-4 border ${
                      Number(item.available_stock) <= 0
                        ? "text-red-600 font-bold"
                        : ""
                    }`}
                  >
                    {item.available_stock}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ThemeModal>
    </>
  )
}

export default AccessoriesStockReport
